import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  getMe,
  login,
  logout,
  loadData,
  saveData,
  validateInvite,
  register,
  changePassword,
  validateResetToken,
  resetPassword,
  syncSocialStats,
  getNotificationCount,
  markNotificationsRead,
  claimReferralXP,
  getPushVapidKey,
  subscribePush,
} from './api';
import {
  getLevelInfo,
  getSocialScore,
  getNetWorth,
  getBudgetStreak,
  getAllBills,
  moIncome,
  moSavings,
  moVar,
  getVarBudget,
  mk,
  STORE_CATALOG,
} from './utils';
import Dashboard from './components/Dashboard';
import SpendingPage from './components/SpendingPage';
import PlanPage from './components/PlanPage';
import YouPage from './components/YouPage';
import Onboarding from './components/Onboarding';
import QuickAdd from './components/QuickAdd';
import CoachPanel from './components/CoachPanel';
import { ErrorBoundary } from './components/ErrorBoundary';
import { DEFAULT_WIDGETS } from './defaultWidgets';
import './App.css';

// Tab ids from before the 4-tab layout, mapped to [tab, sub-tab]
const LEGACY_TABS = {
  dash:     ['today'],
  money:    ['plan', 'budget'],
  progress: ['plan', 'debt'],
  social:   ['you', 'social'],
  settings: ['you', 'settings'],
};

const DEFAULT_STATE = {
  cards: [],
  initCards: [],
  fixedBills: [],
  extra: 0,
  lump: 0,
  method: 'snowball',
  savContrib: 0,
  savGoal: 1000,
  savGoalName: 'Savings',
  savLog: [],
  checkins: [],
  extraGoals: [],
  paidBills: {},
  incomeLog: [],
  budgetCarryover: false,
  varSpend: {},
  savDeposits: {},
  budgetSlider: 50,
  catBudgets: {},
  defaultPaycheck: 0,
  customCats: [],
  disabledCats: [],
  subscriptions: [],
  dashWidgets: DEFAULT_WIDGETS,
  hiddenTabs: [],
  recapSeen: {},
  incomeMode: 'monthly',
  monthlyIncome: 0,
  catchUpSeen: false,
  incomeSources: [],
  dismissedRecurring: [],
  bankBalance: 0,
  bankBalanceDate: null,
  bankCredits: [],
  firstPayDate: null,
  assets: [],
  sinkingFunds: [],
  noSpendDays: [],
  completedQuests: {},
  planSettings: {},
  householdSize: 1,
  planDone: {},
  importHistory: [],
  lastLoginDate: null,
  loginDays: 0,
  referralClaimed: false,
  features: { social: true, gamification: true },
  archivedCardIds: [],
  checklistDismissed: false,
  importRules: [],
  xpSpent: 0,
  storeOwned: [],
  storeEquipped: { accentColor: 'accent_gold', profileTitle: null, confettiStyle: 'fx_classic' },
};

export default function App() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [onboarded, setOnboarded] = useState(false);
  const [checking, setChecking] = useState(true);
  // Home-screen shortcuts (manifest.json) open the app with ?tab=… (old or new ids)
  const initialTab = () => {
    const [t, sub] = String(new URLSearchParams(window.location.search).get('tab') || '').split(':');
    const [nt, ns] = LEGACY_TABS[t] || [t, sub];
    return ['today', 'spending', 'plan', 'you'].includes(nt) ? [nt, ns] : ['today'];
  };
  const [tab, setTabState] = useState(() => initialTab()[0]);
  const [subs, setSubs] = useState(() => {
    const [t, sub] = initialTab();
    return sub ? { [t]: sub } : {};
  });
  // setTab accepts 'plan' or 'plan:debt'. Old tab ids from before the 4-tab layout still work.
  const setTab = useCallback((target) => {
    const [t, sub] = String(target || 'today').split(':');
    const [newTab, newSub] = LEGACY_TABS[t] || [t, sub];
    setTabState(newTab);
    if (sub || newSub) setSubs((p) => ({ ...p, [newTab]: sub || newSub }));
  }, []);
  const setSub = (sub) => setSubs((p) => ({ ...p, [tab]: sub }));
  const [state, setState] = useState(DEFAULT_STATE);
  const [toast, setToast] = useState('');
  const [saving, setSaving] = useState(false);
  const [confettiOn, setConfettiOn] = useState(false);
  const [paydayOn, setPaydayOn] = useState(false);
  const [notifCount, setNotifCount] = useState(0);
  const [levelUpInfo, setLevelUpInfo] = useState(null); // { level, name } for ceremony
  const prevLevelRef = useRef(null);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');
  const stateRef = useRef(state);
  // Save versioning: the server refuses a save based on an older version
  // (another device saved in between), so a stale tab can't overwrite it.
  const versionRef = useRef(null); // version of the data this tab last loaded/saved
  const saveChainRef = useRef(Promise.resolve()); // one save in flight at a time
  const pendingSaveRef = useRef(false); // a debounced save is waiting to run
  const skipSaveRef = useRef(false); // next state change came from the server

  // Detect invite/reset tokens in URL
  const params = new URLSearchParams(window.location.search);
  const inviteToken = params.get('token');
  const resetToken = params.get('reset');

  useEffect(() => {
    getMe().then((res) => {
      if (res.loggedIn) {
        setUser(res.username);
        setIsAdmin(res.isAdmin);
        loadData().then((r) => {
          if (r.data) {
            // Track daily login
            const today = new Date().toISOString().slice(0, 10);
            if (r.data.lastLoginDate !== today) {
              r.data.lastLoginDate = today;
              r.data.loginDays = (r.data.loginDays || 0) + 1;
            }
            // Claim referral XP once (fire-and-forget)
            if (!r.data.referralClaimed) {
              claimReferralXP().then(res => {
                if (res.ok && !res.alreadyClaimed) {
                  setState(s => ({ ...s, referralClaimed: true }));
                }
              }).catch(() => {});
            }
            // Migrate legacy paychecks {moKey:[{id,amt,ts,sourceId?}]} → flat incomeLog
            if (r.data.paychecks && !r.data.incomeLog?.length) {
              const migrated = [];
              Object.entries(r.data.paychecks).forEach(([, entries]) => {
                (entries || []).forEach((e) => {
                  const d = new Date(e.ts || Date.now());
                  migrated.push({
                    id: e.id || 'inc_mig_' + Math.random().toString(36).slice(2),
                    sourceId: e.sourceId || null,
                    amt: e.amt,
                    date: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
                  });
                });
              });
              r.data.incomeLog = migrated.sort((a, b) => a.date.localeCompare(b.date));
            }
            versionRef.current = Number.isInteger(r.version) ? r.version : null;
            setState(() => ({ ...DEFAULT_STATE, ...r.data }));
            setOnboarded(true);
          } else {
            setOnboarded(false);
          }
          setChecking(false);
        });
      } else {
        setChecking(false);
      }
    });
  }, []);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Register service worker + subscribe to push (runs once after login)
  useEffect(() => {
    if (!user) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    (async () => {
      try {
        const { key } = await getPushVapidKey();
        if (!key) return; // push not configured on this server
        const reg = await navigator.serviceWorker.register('/sw.js');
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const existing = await reg.pushManager.getSubscription();
        // Already subscribed: re-send so the server has this device's time zone
        if (existing) { await subscribePush({ ...existing.toJSON(), tz }); return; }
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return;
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(key),
        });
        await subscribePush({ ...sub.toJSON(), tz });
      } catch { /* push not available — silently skip */ }
    })();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = theme === 'dark' ? '' : theme;
    document.body.setAttribute('data-theme', t);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    const accentId = state.storeEquipped?.accentColor || 'accent_gold';
    const item = STORE_CATALOG.find(i => i.id === accentId);
    const color = item?.value || '#ffd93d';
    document.documentElement.style.setProperty('--ylw', color);
  }, [state.storeEquipped?.accentColor]);

  useEffect(() => {
    if (!user || !onboarded) return;
    const flush = () => {
      // keepalive keeps the request alive after the page unloads.
      // It has a ~64 KB body limit — if the payload exceeds it the browser
      // silently drops keepalive and the request is cancelled on unload, so
      // we fall back to a plain fetch which is still better than nothing.
      try {
        saveData(stateRef.current, { keepalive: true }, versionRef.current);
      } catch {
        saveData(stateRef.current, {}, versionRef.current);
      }
    };
    window.addEventListener('beforeunload', flush);
    return () => window.removeEventListener('beforeunload', flush);
  }, [user, onboarded]);

  // Poll notification count every 60 seconds when logged in
  useEffect(() => {
    if (!user || !onboarded) return;
    const poll = () => getNotificationCount().then(r => { if (r.count !== undefined) setNotifCount(r.count); });
    poll();
    const id = setInterval(poll, 60000);
    return () => clearInterval(id);
  }, [user, onboarded]);

  // When any API call gets a 401 the session has expired. Reset auth state so
  // the login screen appears instead of silently dropping saves.
  useEffect(() => {
    const handleExpired = () => {
      setUser(null);
      setIsAdmin(false);
      setOnboarded(false);
      setState(DEFAULT_STATE);
      setToast('Your session expired — please log in again');
    };
    window.addEventListener('session-expired', handleExpired);
    return () => window.removeEventListener('session-expired', handleExpired);
  }, []);

  // Replace local state with the server's copy without saving it back
  const applyServerData = useCallback((r) => {
    skipSaveRef.current = true;
    versionRef.current = Number.isInteger(r.version) ? r.version : null;
    setState({ ...DEFAULT_STATE, ...r.data });
  }, []);

  // Save queue: each save waits for the previous one so it carries the
  // version that save returned.
  const queueSave = useCallback((snapshot) => {
    const run = saveChainRef.current.then(async () => {
      const r = await saveData(snapshot, {}, versionRef.current);
      if (Number.isInteger(r.version) && !r.error) versionRef.current = r.version;
      return r;
    });
    saveChainRef.current = run.catch(() => {});
    return run;
  }, []);

  // Coming back to this tab: pick up changes saved from another device
  useEffect(() => {
    if (!user || !onboarded) return;
    const refresh = () => {
      if (document.visibilityState !== 'visible' || pendingSaveRef.current) return;
      loadData().then((r) => {
        if (pendingSaveRef.current || !r.data || !Number.isInteger(r.version)) return;
        if (versionRef.current !== null && r.version > versionRef.current) {
          applyServerData(r);
          showToast('Loaded your latest changes from another device');
        }
      });
    };
    document.addEventListener('visibilitychange', refresh);
    return () => document.removeEventListener('visibilitychange', refresh);
    // showToast is stable (useCallback with empty deps)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, onboarded, applyServerData]);

  useEffect(() => {
    if (!user || !onboarded) return;
    if (skipSaveRef.current) {
      skipSaveRef.current = false;
      return;
    }
    setSaving(true);
    pendingSaveRef.current = true;
    const t = setTimeout(() => {
      queueSave(state)
        .then((r) => {
          if (r.error === 'conflict' && r.data) {
            applyServerData(r);
            showToast('This account was updated on another device. Loaded the latest version — please redo your last change.');
            return;
          }
          if (r.error) showToast('Save failed — please refresh and log in again');
          // Sync public social stats (non-critical, fire-and-forget)
          try {
            const li = getLevelInfo(state);
            // Level-up ceremony
            if (prevLevelRef.current !== null && li.level > prevLevelRef.current) {
              setLevelUpInfo({ level: li.level, name: li.name });
              triggerConfetti();
            }
            prevLevelRef.current = li.level;
            const streak = getBudgetStreak(
              state.incomeLog, state.varSpend, state.savDeposits,
              state.cards || [], state.fixedBills || [], state.savContrib,
              state.budgetSlider, state.incomeMode, state.monthlyIncome,
              state.subscriptions || []
            );
            const now = new Date();
            const allBills = getAllBills(state.cards || [], state.fixedBills || [], state.subscriptions || []);
            let goodMos = 0, totalMos = 0;
            for (let i = 1; i <= 6; i++) {
              const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
              const k = mk(d.getFullYear(), d.getMonth());
              const inc = state.incomeMode === 'monthly' ? state.monthlyIncome || 0 : moIncome(state.incomeLog || [], k);
              if (!inc) continue;
              totalMos++;
              const savA = moSavings(state.savDeposits || {}, k, state.savContrib || 0);
              const { budget } = getVarBudget(inc, allBills, savA, state.budgetSlider ?? 50);
              if (budget > 0 && moVar(state.varSpend || {}, k) <= budget) goodMos++;
            }
            const budgetAdherence = totalMos > 0 ? goodMos / totalMos : 0;
            const { savingsRate, goalProgress, rankScore } = getSocialScore(state, { streak, xp: li.xp, budgetAdherence });
            syncSocialStats({ level: li.level, xp: li.xp, streak, savingsRate, budgetAdherence, goalProgress, rankScore }).catch(() => {});
          } catch { /* non-critical */ }
        })
        .finally(() => {
          pendingSaveRef.current = false;
          setSaving(false);
        });
    }, 1200);
    return () => {
      clearTimeout(t);
      pendingSaveRef.current = false;
    };
    // showToast is stable (useCallback with empty deps) — omitting it is safe
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, user, onboarded]);

  const showToast = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  }, []);

  const triggerConfetti = useCallback(() => {
    setConfettiOn(true);
    setTimeout(() => setConfettiOn(false), 3500);
  }, []);

  const triggerPayday = useCallback(() => {
    setPaydayOn(true);
    setTimeout(() => setPaydayOn(false), 2200);
  }, []);

  const updateState = useCallback((updates) => {
    setState((s) => ({ ...s, ...updates }));
  }, []);

  const handleLogin = async (username, isAdm, isOnboarded) => {
    setUser(username);
    setIsAdmin(isAdm);
    const r = await loadData();
    if (r.data) {
      versionRef.current = Number.isInteger(r.version) ? r.version : null;
      setState({ ...DEFAULT_STATE, ...r.data });
      setOnboarded(true);
    } else {
      setOnboarded(isOnboarded);
    }
  };

  const handleLogout = async () => {
    await logout();
    setUser(null);
    setState(DEFAULT_STATE);
    setIsAdmin(false);
    setOnboarded(false);
  };

  const handleOnboardingComplete = (finalData) => {
    setState(finalData);
    setOnboarded(true);
  };

  const handlePasswordChange = async (cur, next) => {
    return changePassword(cur, next);
  };

  if (checking) return <div className="loading">Loading...</div>;
  if (!user && resetToken) return <ResetPasswordScreen token={resetToken} />;
  if (!user && inviteToken) return <RegisterScreen token={inviteToken} onRegister={handleLogin} />;
  if (!user) return <LoginScreen onLogin={handleLogin} />;
  if (!onboarded) return <Onboarding username={user} onComplete={handleOnboardingComplete} />;

  const features = state.features || { social: true, gamification: true };
  const tabs = [
    { id: 'today',    label: '🏠 Today',    short: 'Today' },
    { id: 'spending', label: '🧾 Spending', short: 'Spending' },
    { id: 'plan',     label: '🗺️ Plan',     short: 'Plan' },
    { id: 'you',      label: '👤 You',      short: 'You' },
  ];

  const sharedProps = { state, updateState, showToast, triggerConfetti, triggerPayday, setTab, features, setTheme, theme };

  return (
    <div className="app">
      <header id="hdr">
        <div className="hi">
          <div className="ht">
            <div className="htl">
              <h1>💰 My Financial Dashboard</h1>
              <p>Personal Budget · Debt Tracker · Savings</p>
            </div>
            <div className="hst">
              <HeaderStats state={state} />
              {features.social && <button
                className="notif-btn"
                title="Notifications"
                onClick={() => {
                  setTab('you:social');
                  if (notifCount > 0) markNotificationsRead().then(() => setNotifCount(0));
                }}
                style={{ position: 'relative', background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, padding: '4px 8px' }}
              >
                🔔
                {notifCount > 0 && (
                  <span style={{
                    position: 'absolute', top: 0, right: 0,
                    background: '#ff4757', color: '#fff',
                    borderRadius: '50%', fontSize: 10, fontWeight: 700,
                    minWidth: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '0 3px',
                  }}>
                    {notifCount > 9 ? '9+' : notifCount}
                  </span>
                )}
              </button>}
              <button className="logout-btn" onClick={handleLogout}>
                Log Out
              </button>
            </div>
          </div>
          <div className="tabs">
            {tabs.map((t) => (
              <button
                key={t.id}
                className={'tab' + (tab === t.id ? ' on' : '')}
                onClick={() => setTab(t.id)}
              >
                <span className="tab-full">{t.label}</span>
                <span className="tab-short">
                  {t.label.split(' ')[0]}
                  <br />
                  {t.short}
                </span>
              </button>
            ))}
          </div>
        </div>
      </header>
      <div id="bdy">
        <ErrorBoundary key={tab}>
          {tab === 'today'    && <Dashboard {...sharedProps} />}
          {tab === 'spending' && <SpendingPage {...sharedProps} sub={subs.spending} setSub={setSub} />}
          {tab === 'plan'     && <PlanPage {...sharedProps} sub={subs.plan} setSub={setSub} />}
          {tab === 'you' && (
            <YouPage
              {...sharedProps}
              sub={subs.you}
              setSub={setSub}
              username={user}
              settingsProps={{ isAdmin, onPasswordChange: handlePasswordChange }}
            />
          )}
        </ErrorBoundary>
      </div>
      <QuickAdd state={state} updateState={updateState} showToast={showToast} />
      <CoachPanel />
      <Confetti active={confettiOn} style={state.storeEquipped?.confettiStyle || 'fx_classic'} />
      <PaydayAnimation active={paydayOn} />
      {levelUpInfo && features.gamification && <LevelUpCeremony info={levelUpInfo} onClose={() => setLevelUpInfo(null)} />}
      {toast && <div className="toast on">{toast}</div>}
      {saving && <div className="saving-dot" title="Saving..." />}
    </div>
  );
}

function Confetti({ active, style = 'fx_classic' }) {
  const piecesRef = useRef([]);
  if (active && !piecesRef.current.length) {
    if (style === 'fx_money') {
      piecesRef.current = Array.from({ length: 22 }, (_, i) => ({
        id: i, left: Math.random() * 100,
        delay: (Math.random() * 0.9).toFixed(2), dur: (1.6 + Math.random()).toFixed(2),
        em: ['💵', '💰', '🪙'][i % 3], size: 22 + Math.floor(Math.random() * 10), isEmoji: true,
      }));
    } else if (style === 'fx_stars') {
      piecesRef.current = Array.from({ length: 40 }, (_, i) => ({
        id: i, left: Math.random() * 100,
        delay: (Math.random() * 0.9).toFixed(2), dur: (1.6 + Math.random()).toFixed(2),
        em: '⭐', size: 14 + Math.floor(Math.random() * 8), isEmoji: true,
      }));
    } else if (style === 'fx_hearts') {
      piecesRef.current = Array.from({ length: 30 }, (_, i) => ({
        id: i, left: Math.random() * 100,
        delay: (Math.random() * 0.9).toFixed(2), dur: (1.6 + Math.random()).toFixed(2),
        em: ['❤️', '💛', '💜', '💙'][i % 4], size: 18 + Math.floor(Math.random() * 8), isEmoji: true,
      }));
    } else if (style === 'fx_diamonds') {
      piecesRef.current = Array.from({ length: 28 }, (_, i) => ({
        id: i, left: Math.random() * 100,
        delay: (Math.random() * 0.9).toFixed(2), dur: (1.6 + Math.random()).toFixed(2),
        em: '💎', size: 20 + Math.floor(Math.random() * 10), isEmoji: true,
      }));
    } else {
      piecesRef.current = Array.from({ length: 55 }, (_, i) => ({
        id: i, left: Math.random() * 100,
        delay: (Math.random() * 0.9).toFixed(2), dur: (1.6 + Math.random()).toFixed(2),
        color: ['#ffd93d', '#6bcb77', '#4d96ff', '#ff6bdf', '#ff6b6b', '#7C6FF7'][i % 6],
        size: 5 + Math.floor(Math.random() * 7), round: i % 3 === 0, isEmoji: false,
      }));
    }
  }
  if (!active) { piecesRef.current = []; return null; }
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', zIndex: 9999, overflow: 'hidden' }}>
      {piecesRef.current.map((p) => (
        <div
          key={p.id}
          style={p.isEmoji ? {
            position: 'absolute', left: p.left + '%', top: '-30px',
            fontSize: p.size, animation: `conffall ${p.dur}s ease-in ${p.delay}s forwards`,
          } : {
            position: 'absolute', left: p.left + '%', top: '-20px',
            width: p.size, height: p.size, background: p.color,
            borderRadius: p.round ? '50%' : 2,
            animation: `conffall ${p.dur}s ease-in ${p.delay}s forwards`,
          }}
        >
          {p.isEmoji ? p.em : null}
        </div>
      ))}
    </div>
  );
}

function PaydayAnimation({ active }) {
  const coinsRef = useRef([]);
  const COIN_EMOJIS = ['💵', '🪙', '💰', '💵', '🪙'];
  if (active && !coinsRef.current.length) {
    coinsRef.current = Array.from({ length: 18 }, (_, i) => ({
      id: i,
      left: 10 + Math.random() * 80,
      delay: (Math.random() * 0.6).toFixed(2),
      dur: (0.8 + Math.random() * 0.8).toFixed(2),
      em: COIN_EMOJIS[i % COIN_EMOJIS.length],
      size: 18 + Math.floor(Math.random() * 14),
    }));
  }
  if (!active) { coinsRef.current = []; return null; }
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', zIndex: 9998, overflow: 'hidden' }}>
      {coinsRef.current.map((c) => (
        <div
          key={c.id}
          style={{
            position: 'absolute',
            left: c.left + '%',
            top: '-30px',
            fontSize: c.size,
            animation: `coinfall ${c.dur}s ease-in ${c.delay}s forwards`,
          }}
        >
          {c.em}
        </div>
      ))}
    </div>
  );
}

function LevelUpCeremony({ info, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ textAlign: 'center', animation: 'level-pop .5s cubic-bezier(.34,1.56,.64,1)' }}
      >
        <div style={{ fontSize: 72, marginBottom: 8 }}>⬆️</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ylw)', letterSpacing: 3, textTransform: 'uppercase', marginBottom: 8 }}>Level Up!</div>
        <div style={{ fontSize: 52, fontWeight: 900, color: '#fff', marginBottom: 4 }}>Level {info.level}</div>
        <div style={{ fontSize: 20, color: 'var(--ylw)', fontWeight: 800, marginBottom: 24 }}>{info.name}</div>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,.5)' }}>Tap anywhere to continue</div>
      </div>
    </div>
  );
}

function HeaderStats({ state }) {
  const totBal = (state.cards || []).reduce((s, c) => s + +c.bal, 0);
  const totMin = (state.cards || []).reduce((s, c) => s + +c.min, 0);
  const { netWorth } = getNetWorth(state);
  const f0 = (n) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(n);
  return (
    <>
      <div className="hsi">
        <div className="hsl">Total Debt</div>
        <div className="hsv">{f0(totBal)}</div>
      </div>
      <div className="hsi">
        <div className="hsl">Min/Month</div>
        <div className="hsv">{f0(totMin)}</div>
      </div>
      <div className="hsi">
        <div className="hsl">Net Worth</div>
        <div className="hsv" style={{ color: netWorth >= 0 ? 'var(--grn)' : 'var(--red)' }}>{f0(netWorth)}</div>
      </div>
    </>
  );
}

function ResetPasswordScreen({ token }) {
  const [status, setStatus] = useState(null); // null=loading, true=valid, string=error
  const [username, setUsername] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confPw, setConfPw] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    validateResetToken(token).then((r) => {
      if (r.valid) {
        setUsername(r.username);
        setStatus(true);
      } else {
        setStatus(r.error || 'Invalid or expired reset link');
      }
    });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPw !== confPw) { setError('Passwords do not match'); return; }
    if (newPw.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    const res = await resetPassword(token, newPw);
    setLoading(false);
    if (res.ok) {
      setDone(true);
      window.history.replaceState({}, '', '/');
    } else {
      setError(res.error || 'Reset failed');
    }
  };

  if (status === null) return <div className="loading">Checking reset link...</div>;

  if (status !== true) {
    return (
      <div className="login-wrap">
        <div className="login-card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>❌</div>
          <h2 className="login-title">Invalid Reset Link</h2>
          <p className="login-sub">{status}</p>
          <a href="/" className="lbtn" style={{ display: 'block', marginTop: 16, textDecoration: 'none', textAlign: 'center' }}>Back to Login</a>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="login-wrap">
        <div className="login-card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
          <h2 className="login-title">Password Updated</h2>
          <p className="login-sub">Your password has been changed. You can now log in with your new password.</p>
          <a href="/" className="lbtn" style={{ display: 'block', marginTop: 16, textDecoration: 'none', textAlign: 'center' }}>Go to Login</a>
        </div>
      </div>
    );
  }

  return (
    <div className="login-wrap">
      <div className="login-card">
        <h1 className="login-title">🔑 Reset Password</h1>
        <p className="login-sub">Setting a new password for <strong>{username}</strong></p>
        <form onSubmit={handleSubmit}>
          <div className="lf">
            <label>New Password</label>
            <input
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              autoComplete="new-password"
              required
            />
          </div>
          <div className="lf">
            <label>Confirm Password</label>
            <input
              type="password"
              value={confPw}
              onChange={(e) => setConfPw(e.target.value)}
              autoComplete="new-password"
              required
            />
          </div>
          {error && <div className="lerr">{error}</div>}
          <button type="submit" className="lbtn" disabled={loading}>
            {loading ? 'Saving...' : 'Set New Password'}
          </button>
        </form>
      </div>
    </div>
  );
}

function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const res = await login(username, password);
    setLoading(false);
    if (res.ok) onLogin(res.username, res.isAdmin, res.onboarded);
    else setError(res.error || 'Invalid credentials');
  };

  return (
    <div className="login-wrap">
      <div className="login-card">
        <h1 className="login-title">💰 Finance Dashboard</h1>
        <p className="login-sub">Sign in to access your dashboard</p>
        <form onSubmit={handleSubmit}>
          <div className="lf">
            <label>Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
            />
          </div>
          <div className="lf">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>
          {error && <div className="lerr">{error}</div>}
          <button type="submit" className="lbtn" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

function RegisterScreen({ token, onRegister }) {
  const [valid, setValid] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    validateInvite(token).then((r) => setValid(r.valid ? true : r.error || 'Invalid link'));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    const res = await register(token, username, password);
    setLoading(false);
    if (res.ok) {
      window.history.replaceState({}, '', '/');
      onRegister(res.username, res.isAdmin, res.onboarded);
    } else setError(res.error || 'Registration failed');
  };

  if (valid === null) return <div className="loading">Checking invite...</div>;
  if (valid !== true)
    return (
      <div className="login-wrap">
        <div className="login-card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>❌</div>
          <h2 className="login-title">Invalid Invite</h2>
          <p className="login-sub">{valid}</p>
        </div>
      </div>
    );

  return (
    <div className="login-wrap">
      <div className="login-card">
        <h1 className="login-title">💰 Create Account</h1>
        <p className="login-sub">You've been invited! Set up your account to get started.</p>
        <form onSubmit={handleSubmit}>
          <div className="lf">
            <label>Choose a Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </div>
          <div className="lf">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <div className="lf">
            <label>Confirm Password</label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </div>
          {error && <div className="lerr">{error}</div>}
          <button type="submit" className="lbtn" disabled={loading}>
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>
      </div>
    </div>
  );
}

// Convert a base64url VAPID public key to a Uint8Array for the Web Push API
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}
